import { cloneElement, isValidElement, type ReactNode } from "react";

/**
 * Garde `onInteractOutside` pour les 3 coquilles Popover de partage
 * (`ShareCasePopover`/`ShareConversationPopover`/`ShareTemplatePopover`).
 *
 * Deux sources d'interaction à ignorer explicitement :
 *
 * 1. `[data-radix-popper-content-wrapper]` — le `<Select>` de rôle (Lecture/
 *    Édition) dans `ShareControl` est un Popper Radix, portalisé sous
 *    `document.body` (pas un descendant DOM du panneau). Sans ce garde, ouvrir
 *    ce menu fermerait le panneau de partage par erreur.
 *
 * 2. `[data-share-anchor]` (posé par `markShareAnchor` sur le bouton ⋮) —
 *    ces 3 panneaux s'ancrent via `PopoverAnchor` (pas `PopoverTrigger`, car
 *    l'ouverture est pilotée par l'item « Partager » du menu ⋮, pas par un
 *    clic direct sur le bouton). Radix EXCLUT automatiquement le `Trigger` de
 *    sa détection "interaction extérieure", mais PAS l'`Anchor` — ce n'est
 *    qu'un repère de positionnement à ses yeux. Or quand le `DropdownMenu` ⋮
 *    se ferme (sélection de l'item « Partager »), Radix restaure le focus sur
 *    CE MÊME bouton ⋮ (son ancien trigger) : sans ce garde, ce retour de focus
 *    est lu par le Popover comme un focus extérieur et le referme aussitôt
 *    (panneau qui « disparaît », signalé par l'utilisateur 2026-07-07).
 */
export function guardNestedPopoverInteractOutside(event: Event): void {
  const target = event.target as HTMLElement | null;
  if (target?.closest("[data-radix-popper-content-wrapper], [data-share-anchor]")) {
    event.preventDefault();
  }
}

/**
 * Marque l'élément d'ancrage (bouton ⋮) avec `data-share-anchor` sans ajouter
 * de wrapper DOM — `cloneElement` sur le `children` que les 3 panneaux
 * reçoivent (`<DropdownMenuTrigger asChild><Button/></DropdownMenuTrigger>`),
 * qui transfère la prop jusqu'au `<button>` réel via le `Slot` de Radix.
 * Consommé par `guardNestedPopoverInteractOutside` (cf. commentaire ci-dessus).
 */
export function markShareAnchor(children: ReactNode): ReactNode {
  if (!isValidElement<Record<string, unknown>>(children)) return children;
  return cloneElement(children, { "data-share-anchor": "" });
}
